import { useEffect, useState } from "react";

import { Link } from "react-router-dom";

import api from "@/lib/api";
import { useLang } from "@/lib/i18n.jsx";
import { marketingHref } from "@/lib/localePath";
import SocialAffiliatesBanner from "@/components/SocialAffiliatesBanner";

/**
 * Footer unico del sito — chi siamo, dove siamo registrati, come scriverci.
 *
 * Prima di questo componente il fondo pagina esisteva solo sulla home, con la
 * riga «© 2026 Hostaly»: nessuna ragione sociale, nessuna sede, nessuna
 * partita IVA e nessun numero di registrazione. Chi pubblica un servizio
 * online deve dire chi è — Companies Act 2006 s.82 (numero, luogo di
 * registrazione e sede legale sul sito), E-Commerce Regulations 2002 reg. 6 e,
 * per l'Italia, l'informativa societaria — e lo stesso vale prima di qualsiasi
 * discorso sul paese della società: la mancanza c'era già.
 *
 * I dati **non** sono scritti qui. Arrivano da `GET /api/site/identity`, che
 * legge `resolve_seller_fiscal`: la stessa funzione che stampa il cedente sui
 * rendiconti delle commissioni. Una sola fonte per fatture e sito, quindi
 * passare a una società britannica (o restare in Italia) cambia i dati in un
 * posto e non le due copie in disaccordo.
 *
 * Regola di rendering: ogni riga compare **solo** se il suo dato esiste. Un
 * footer che stampa «P.IVA » seguito dal nulla è peggio di un footer senza
 * P.IVA, e una configurazione a metà (ragione sociale senza numero) deve
 * restare leggibile invece di mostrare separatori orfani.
 */

// Una richiesta per pagina caricata, non una per componente: la home, i
// termini e la privacy finiscono nello stesso documento e devono mostrare gli
// stessi dati. La promessa vive nel modulo (non nello stato React) perché il
// footer si smonta e si rimonta a ogni navigazione, e senza cache ogni pagina
// rifarebbe la stessa chiamata per un dato che non cambia.
let identityRequest = null;
const loadIdentity = () => {
  if (!identityRequest) {
    identityRequest = api
      .get("/site/identity")
      .then((r) => r.data || {})
      .catch(() => {
        // Un errore di rete non deve togliere il footer: si torna al nome e
        // ai link. La promessa fallita però non resta in cache, così la
        // prossima navigazione riprova invece di mostrare il fallback per
        // tutta la sessione.
        identityRequest = null;
        return {};
      });
  }
  return identityRequest;
};

//: Svuota la cache della richiesta. Esiste per i test, che montano più footer
//: con identità diverse nello stesso processo: senza, il secondo caso
//: leggerebbe la risposta del primo.
export const __resetIdentityCache = () => {
  identityRequest = null;
};

const text = (v) => String(v ?? "").trim();

export default function SiteFooter({ social = false, className = "" }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [identity, setIdentity] = useState({});

  useEffect(() => {
    let alive = true;
    loadIdentity().then((data) => {
      if (alive) setIdentity(data || {});
    });
    return () => {
      alive = false;
    };
  }, []);

  const name = text(identity.legal_name) || "Hostaly";
  const country = text(identity.country);
  const vatNumber = text(identity.vat_number);
  const address = [text(identity.address), text(identity.city)].filter(Boolean).join(", ");
  const registrationNumber = text(identity.registration_number);
  const registrationPlace = text(identity.registration_place);
  // "P.IVA" è italiano: a un'azienda britannica si dice VAT, e la sigla non
  // cambia con la lingua dell'interfaccia ma con il paese del venditore.
  const vatLabel = country && country !== "IT" ? "VAT" : "P.IVA";
  const vat = vatNumber ? `${vatLabel} ${country}${vatNumber}` : "";
  // Numero e luogo di registrazione sono due dati distinti e non sempre
  // arrivano insieme. Con il luogo accanto basta «n.»; da solo, il numero ha
  // bisogno dell'etichetta per voler dire qualcosa.
  const registration = [
    registrationPlace,
    registrationNumber
      ? `${registrationPlace ? "n. " : `${IT ? "Numero di registrazione" : "Registration number"} `}${registrationNumber}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const email = text(identity.email);

  return (
    <footer className={`border-t border-border py-8 ${className}`} data-testid="site-footer">
      {social ? (
        <div className="max-w-7xl mx-auto px-6">
          <SocialAffiliatesBanner className="mb-8" />
        </div>
      ) : null}
      <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-start justify-between gap-x-8 gap-y-6 text-sm text-muted-foreground">
        <div className="space-y-1" data-testid="site-footer-identity">
          <div className="text-foreground/90">
            © {new Date().getFullYear()} {name}
          </div>
          {address ? <div data-testid="site-footer-address">{address}</div> : null}
          {registration ? (
            <div data-testid="site-footer-registration">{registration}</div>
          ) : null}
          {vat ? <div data-testid="site-footer-vat">{vat}</div> : null}
          {email ? (
            <a
              href={`mailto:${email}`}
              className="inline-block hover:text-primary transition-colors"
              data-testid="site-footer-email"
            >
              {email}
            </a>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* Il kit «Preferisco Hostaly» non sta nel menu (non e' una pagina
              di prodotto) ma deve restare raggiungibile da chi pubblica: un
              link in fondo costa una riga e non distrae nessuno. */}
          {/* La pagina del channel manager serve a chi arriva da Vik
              cercando se deve cambiare gestionale: sta in fondo perche' non
              e' una pagina di prodotto, ma se non c'e' un link non la trova
              nessuno tranne Google. */}
          <Link to={marketingHref("channelManager", lang)} className="hover:text-primary transition-colors" data-testid="footer-channel-manager-link">
            Channel manager
          </Link>
          <Link to="/kit-social" className="hover:text-primary transition-colors" data-testid="footer-kit-link">
            Kit social
          </Link>
          {/* Termini e segnalazione: il contratto host era scaricabile solo
              dal form di iscrizione e non esisteva un canale pubblico per
              segnalare un contenuto illecito. Due link costano poco e sono
              il primo posto in cui si cercano. */}
          <Link to={marketingHref("terms", lang)} className="hover:text-primary transition-colors" data-testid="footer-termini-link">
            {IT ? "Termini" : "Terms"}
          </Link>
          <Link to={marketingHref("guestTerms", lang)} className="hover:text-primary transition-colors" data-testid="footer-condizioni-link">
            {IT ? "Condizioni ospiti" : "Guest terms"}
          </Link>
          {/* Privacy: la chiedono il GDPR e gli store, e chi cerca «hostaly
              privacy» deve trovarla da qui, non solo dall'app. */}
          <Link to={marketingHref("privacy", lang)} className="hover:text-primary transition-colors" data-testid="footer-privacy-link">
            {IT ? "Privacy" : "Privacy"}
          </Link>
          <Link to={marketingHref("report", lang)} className="hover:text-primary transition-colors" data-testid="footer-segnala-link">
            {IT ? "Segnala un contenuto" : "Report content"}
          </Link>
        </div>
      </div>
    </footer>
  );
}
